import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Loader2, Heart, Check, X, MessageCircleQuestion, Crown, Lock, Lightbulb } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import TutorChatDialog from '../components/TutorChatDialog';

const moduleNames = {
  grammar_cloze: 'Grammar Cloze',
  vocabulary_cloze: 'Vocabulary Cloze',
  grammar_mcq: 'Grammar MCQ',
  vocabulary_mcq: 'Vocabulary MCQ',
  synthesis: 'Synthesis & Transformation',
  editing: 'Editing',
  comprehension_cloze: 'Comprehension Cloze',
  antonyms_synonyms: 'Antonyms & Synonyms',
  regular_irregular_verbs: 'Regular & Irregular Verbs'
};

// Per-module sub-topic configuration for the chooser UI and independent progress tracking
const SUBTOPIC_CONFIGS = {
  antonyms_synonyms: {
    prompt: 'Which would you like to drill?',
    options: [
      { key: 'synonyms', label: 'Synonyms', desc: <>Words that mean <span className="font-semibold">the same</span> — closest in meaning.</>, colors: 'border-teal-200 bg-teal-50 hover:border-teal-500 hover:bg-teal-100', title: 'text-teal-700', badge: 'bg-teal-100 text-teal-700' },
      { key: 'antonyms', label: 'Antonyms', desc: <>Words that mean <span className="font-semibold">the opposite</span> — closest opposite.</>, colors: 'border-indigo-200 bg-indigo-50 hover:border-indigo-500 hover:bg-indigo-100', title: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700' }
    ]
  },
  regular_irregular_verbs: {
    prompt: 'Which verbs would you like to practise?',
    options: [
      { key: 'regular', label: 'Regular Verbs', desc: <>Verbs ending in <span className="font-semibold">-ed</span> in past tense (walk → walked).</>, colors: 'border-emerald-200 bg-emerald-50 hover:border-emerald-500 hover:bg-emerald-100', title: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
      { key: 'irregular', label: 'Irregular Verbs', desc: <>Verbs with <span className="font-semibold">unique</span> past forms (go → went, eat → ate).</>, colors: 'border-orange-200 bg-orange-50 hover:border-orange-500 hover:bg-orange-100', title: 'text-orange-700', badge: 'bg-orange-100 text-orange-700' }
    ]
  }
};

const PracticePage = () => {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(null); // {used, limit, level, question_type}
  const [quota, setQuota] = useState(null); // {used, limit, remaining, is_subscribed}
  // Sub-topic mode: e.g. 'synonyms'/'antonyms'/'regular'/'irregular'/'mixed'/null (chooser)
  const [subMode, setSubMode] = useState(null);
  // The subtopic actually used for the currently-displayed question (for progress reporting)
  const [currentSubtopic, setCurrentSubtopic] = useState(null);
  const subConfig = SUBTOPIC_CONFIGS[moduleId] || null;
  const needsSubtopic = !!subConfig;

  // Prof.Scaly hint system
  const [hintLevel, setHintLevel] = useState(0);
  const [currentHint, setCurrentHint] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [correctStreak, setCorrectStreak] = useState(0);


  const fetchQuota = async () => {
    try {
      const q = await api.getQuota(moduleId, user.level);
      setQuota(q);
      // If already exceeded on page load, show the exceeded card
      if (q.quota_exceeded) {
        setQuotaExceeded({
          used: q.used,
          limit: q.limit,
          level: q.level,
          question_type: q.question_type
        });
      }
    } catch (error) {
      console.error('Error fetching quota:', error);
    }
  };

  useEffect(() => {
    fetchQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, user?.level]);

  const generateNewQuestion = async () => {
    setLoading(true);
    setShowResult(false);
    setUserAnswer('');
    setIsSaved(false);
    setQuotaExceeded(null);
    // Reset hint state for new question
    setHintLevel(0);
    setCurrentHint(null);
    setWrongAttempts(0);
    
    // For subtopic-required modules: pick subtopic for this round based on the chosen mode
    let subtopicForCall = null;
    if (needsSubtopic && subConfig) {
      const validKeys = subConfig.options.map(o => o.key);
      if (subMode && validKeys.includes(subMode)) {
        subtopicForCall = subMode;
      } else if (subMode === 'mixed') {
        subtopicForCall = validKeys[Math.floor(Math.random() * validKeys.length)];
      }
    }
    setCurrentSubtopic(subtopicForCall);
    
    try {
      const data = await api.generateQuestion(moduleId, user.level, undefined, subtopicForCall || undefined);
      setQuestion(data);
      // Prefer server-echoed subtopic (custom questions may carry their own)
      if (data?.subtopic) setCurrentSubtopic(data.subtopic);
      // Refresh quota after successful generation
      fetchQuota();
    } catch (error) {
      console.error('Error generating question:', error);
      // Handle quota exceeded (402)
      if (error.response?.status === 402) {
        const detail = error.response.data?.detail;
        setQuotaExceeded(detail || { used: 50, limit: 50, level: user.level, question_type: moduleId });
        setQuestion(null);
      } else {
        toast.error('Failed to generate question');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    const correct = userAnswer.trim().toLowerCase() === question.correct_answer.trim().toLowerCase();
    setIsCorrect(correct);
    
    // Only reveal result if correct; if wrong, allow retry/hint
    if (correct) {
      setShowResult({ givingUp: true }); // Show full result when correct
    }

    // Update streak counters
    if (correct) {
      const newStreak = correctStreak + 1;
      setCorrectStreak(newStreak);
      setWrongAttempts(0);
      setHintLevel(0);
      setCurrentHint(null);
    } else {
      const newWrongAttempts = wrongAttempts + 1;
      setWrongAttempts(newWrongAttempts);
      setCorrectStreak(0);
      
      // Show inline "Try again" feedback without blocking input
      toast.error('Not quite right. Try again or get a hint!', { duration: 3000 });
    }

    // Record progress and check for streak increment
    try {
      const result = await api.recordProgress(moduleId, user.level, correct, currentSubtopic || undefined);
      const streak = result?.streak;
      if (streak?.incremented_today) {
        const days = streak.current_streak;
        const isNewLongest = days === streak.longest_streak && days > 1;
        toast.success(
          isNewLongest
            ? `🔥 ${days}-day streak! New personal best!`
            : days === 1
            ? '🔥 Streak started! Come back tomorrow to keep it going.'
            : `🔥 ${days}-day streak! Keep it going!`,
          { duration: 4000 }
        );
      }
      // ---- Monster Battle feedback ----
      const monster = result?.monster;
      if (monster && !monster.skipped) {
        const monsterName = {
          dirty: 'The Dirty Monster',
          grey: 'The Grey Monster',
          plastic: 'The Plastic Monster'
        }[monster.active_monster] || 'The Monster';
        
        if (monster.damage_events && monster.damage_events.some(e => e.source === 'correct_answer')) {
          toast.success(`⚔️ −1 damage to ${monsterName}! (${monster.total_damage}/${monster.total_hp})`, {
            duration: 2500
          });
        }
        (monster.badges_earned_this_attempt || []).forEach(b => {
          toast.success(`${b.emoji} Badge earned: ${b.name}! (+${b.damage_applied} bonus damage)`, {
            duration: 5000
          });
        });
        if (monster.defeated_this_attempt) {
          const prev = {
            dirty: 'The Dirty Monster',
            grey: 'The Grey Monster',
            plastic: 'The Plastic Monster'
          }[monster.previous_monster] || 'The Monster';
          toast.success(`🎉 ${prev} DEFEATED! Freeing Singapore, one topic at a time.`, { duration: 7000 });
        }
      }
    } catch (error) {
      console.error('Error recording progress:', error);
    }
  };

  const requestHint = async () => {
    if (showResult?.givingUp || hintLoading) return; // No hints after giving up or while loading
    
    setHintLoading(true);
    setHintLevel(hintLevel + 1);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scaly/hint`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question_type: moduleId,
            question_id: question.id,
            hint_level: hintLevel + 1,
            question_data: {
              question_text: question.question,
              options: question.options ? question.options.join(', ') : ''
            }
          })
        }
      );

      if (response.ok) {
        const hint = await response.json();
        setCurrentHint(hint);
        
        // Show hint as toast notification
        toast.info(hint.hint_text, { duration: 8000 });
      }
    } catch (error) {
      console.error('Failed to get hint:', error);
      toast.error('Failed to load hint. Please try again.');
    } finally {
      setHintLoading(false);
    }
  };

  const saveFavorite = async () => {
    if (!question) return;
    
    try {
      await api.saveFavorite(question.id, question);
      setIsSaved(true);
      toast.success('Question saved to favorites!');
    } catch (error) {
      console.error('Error saving favorite:', error);
      toast.error('Failed to save question');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="practice-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="practice-title">
                  {moduleNames[moduleId]}
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-500" data-testid="practice-level">{user?.level}</p>
                  {needsSubtopic && subMode && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          subMode === 'mixed'
                            ? 'bg-amber-100 text-amber-700'
                            : subConfig?.options.find(o => o.key === subMode)?.badge || 'bg-slate-100 text-slate-700'
                        }`}
                        data-testid="antsyn-mode-badge"
                      >
                        {subMode === 'mixed'
                          ? 'Mixed'
                          : subConfig?.options.find(o => o.key === subMode)?.label || subMode}
                      </span>
                      <button
                        onClick={() => { setSubMode(null); setQuestion(null); setShowResult(false); }}
                        className="text-xs text-emerald-600 hover:text-emerald-700 underline"
                        data-testid="antsyn-mode-change"
                      >
                        change
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Quota progress banner */}
        {quota && !quotaExceeded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
            data-testid="quota-banner"
          >
            {quota.is_subscribed ? (
              <div className="bg-gradient-to-r from-amber-50 to-emerald-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="bg-amber-500 rounded-full p-2">
                  <Crown className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-bold text-slate-800 text-sm" data-testid="quota-premium-text">
                    Premium · Unlimited practice
                  </p>
                  <p className="text-xs text-slate-600">Practice as much as you like at {user?.level}!</p>
                </div>
              </div>
            ) : (
              <div className={`rounded-2xl p-4 border-2 ${
                quota.remaining <= 10
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-white border-slate-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-heading font-bold text-slate-800" data-testid="quota-banner-title">
                      Free practices remaining
                    </p>
                    {quota.remaining <= 10 && (
                      <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">Running Low</span>
                    )}
                  </div>
                  <p className="text-sm font-bold text-slate-700" data-testid="quota-banner-count">
                    <span className={quota.remaining <= 10 ? 'text-amber-700' : 'text-emerald-700'}>{quota.remaining}</span>
                    <span className="text-slate-500"> / {quota.limit} left</span>
                  </p>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      quota.remaining <= 10 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, (quota.used / quota.limit) * 100)}%` }}
                    data-testid="quota-banner-bar"
                  />
                </div>
                {quota.remaining <= 10 && (
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-700">
                      Subscribe for unlimited practice at {user?.level}!
                    </p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-3 py-1.5 font-bold whitespace-nowrap"
                      data-testid="quota-banner-upgrade"
                    >
                      <Crown className="w-3 h-3 inline mr-1" strokeWidth={2.5} />
                      Upgrade · SGD $2/yr
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {quotaExceeded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
            data-testid="quota-exceeded-card"
          >
            <Card className="p-8 rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-emerald-50">
              <div className="text-center">
                <div className="bg-amber-500 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-white" strokeWidth={2.5} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-800 mb-2" data-testid="quota-exceeded-title">
                  Free Limit Reached
                </h2>
                <p className="text-slate-600 mb-4">
                  You've used all <span className="font-bold text-amber-700">{quotaExceeded.limit || 50} free practices</span> for <span className="font-bold">{moduleNames[moduleId]}</span> at <span className="font-bold">{user?.level}</span>.
                </p>
                <div className="bg-white rounded-2xl p-4 mb-6 max-w-md mx-auto">
                  <p className="text-sm text-slate-600 mb-1">Unlock unlimited practice for</p>
                  <p className="text-3xl font-heading font-bold text-emerald-600 mb-1">SGD $2.00</p>
                  <p className="text-xs text-slate-500">/ year · All 7 topics at {user?.level}</p>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-bold tactile-button border-amber-700"
                  data-testid="quota-upgrade-button"
                >
                  <Crown className="w-5 h-5 mr-2" strokeWidth={2.5} />
                  Upgrade to Premium
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {!question && !loading && !quotaExceeded && needsSubtopic && !subMode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-8"
            data-testid="antsyn-mode-chooser"
          >
            <div className="text-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
                alt="Prof. Scaly"
                className="w-24 h-24 mx-auto mb-4 object-contain"
              />
              <h2 className="text-3xl font-heading font-bold text-slate-800 mb-2">{subConfig.prompt}</h2>
              <p className="text-slate-600">Pick a mode to start practising — your progress is tracked separately for each.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {subConfig.options.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSubMode(opt.key)}
                  className={`p-6 rounded-3xl border-2 transition-all text-left tactile-button ${opt.colors}`}
                  data-testid={`antsyn-mode-${opt.key}`}
                >
                  <div className={`text-2xl font-heading font-bold mb-1 ${opt.title}`}>{opt.label}</div>
                  <p className="text-sm text-slate-600">{opt.desc}</p>
                </button>
              ))}
              <button
                onClick={() => setSubMode('mixed')}
                className="p-6 rounded-3xl border-2 border-amber-200 bg-amber-50 hover:border-amber-500 hover:bg-amber-100 transition-all text-left tactile-button"
                data-testid="antsyn-mode-mixed"
              >
                <div className="text-2xl font-heading font-bold text-amber-700 mb-1">Mixed</div>
                <p className="text-sm text-slate-600">Alternate between both sub-topics each round.</p>
              </button>
            </div>
          </motion.div>
        )}

        {!question && !loading && !quotaExceeded && (!needsSubtopic || subMode) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-32 h-32 mx-auto mb-6 object-contain"
            />
            <h2 className="text-3xl font-heading font-bold text-slate-800 mb-4">Ready to practice?</h2>
            <p className="text-slate-600 mb-8">Click below to generate your first question!</p>
            <Button
              onClick={generateNewQuestion}
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg font-bold tactile-button border-emerald-700"
              data-testid="generate-first-question-button"
            >
              Generate Question
            </Button>
          </motion.div>
        )}

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
            data-testid="loading-state"
          >
            <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600 font-semibold">Prof.Scaly is thinking...</p>
          </motion.div>
        )}

        {question && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8 rounded-3xl border-2 border-slate-200" data-testid="question-card">
              <div className="mb-6">
                <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-bold mb-4" data-testid="question-topic">
                  {question.topic}
                </div>
                <p className="text-lg text-slate-800 whitespace-pre-wrap" data-testid="question-text">
                  {question.question_text}
                </p>
              </div>

              {question.options && (
                <div className="space-y-3 mb-6">
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => setUserAnswer(option)}
                      className={`w-full text-left p-4 rounded-2xl border-2 font-semibold transition-all ${
                        userAnswer === option
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
                      }`}
                      disabled={showResult?.givingUp}
                      data-testid={`answer-option-${index}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {!question.options && (
                <div className="mb-6">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Your Answer:</label>
                  <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    className="w-full p-4 rounded-2xl border-2 border-slate-200 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="Type your answer here..."
                    disabled={showResult?.givingUp}
                    data-testid="answer-input"
                  />
                </div>
              )}

              {!showResult?.givingUp && (
                <div className="space-y-3">
                  {/* Hint button - show after first wrong attempt */}
                  {wrongAttempts > 0 && hintLevel < 3 && (
                    <Button
                      onClick={requestHint}
                      disabled={hintLoading}
                      className="w-full bg-amber-100 hover:bg-amber-200 text-amber-800 border-2 border-amber-300 rounded-full py-4 font-bold flex items-center justify-center gap-2"
                      data-testid="hint-button"
                    >
                      {hintLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Prof.Scaly is thinking...
                        </>
                      ) : (
                        <>
                          <Lightbulb className="w-5 h-5" />
                          Need a hint? ({3 - hintLevel} left)
                        </>
                      )}
                    </Button>
                  )}

                  {/* Show "Give Up" button after wrong attempts */}
                  {wrongAttempts > 0 && (
                    <Button
                      onClick={() => {
                        setShowResult({ givingUp: true });
                      }}
                      variant="outline"
                      className="w-full rounded-full text-slate-600"
                      data-testid="give-up-button"
                    >
                      Show me the answer
                    </Button>
                  )}

                  <div className="flex gap-3">
                    <Button
                      onClick={checkAnswer}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                      data-testid="check-answer-button"
                    >
                      {wrongAttempts > 0 ? 'Try Again' : 'Check Answer'}
                    </Button>
                    <Button
                      onClick={saveFavorite}
                      variant="outline"
                    className="rounded-full px-6"
                    disabled={isSaved}
                    data-testid="save-favorite-button"
                  >
                    <Heart className={`w-5 h-5 ${isSaved ? 'fill-pink-500 text-pink-500' : ''}`} />
                  </Button>
                </div>
                </div>
              )}

              <AnimatePresence>
                {showResult?.givingUp && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="mt-6"
                  >
                    {/* Show full result when user gives up or answers correctly */}
                    <div className={`p-6 rounded-2xl border-2 ${
                      isCorrect
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`} data-testid="answer-result">
                      <div className="flex items-center gap-3 mb-3">
                        {isCorrect ? (
                          <div className="bg-green-500 rounded-full p-2">
                            <Check className="w-6 h-6 text-white" strokeWidth={3} />
                          </div>
                        ) : (
                          <div className="bg-red-500 rounded-full p-2">
                            <X className="w-6 h-6 text-white" strokeWidth={3} />
                          </div>
                        )}
                        <h3 className={`text-2xl font-heading font-bold ${
                          isCorrect ? 'text-green-700' : 'text-red-700'
                        }`} data-testid="result-status">
                          {isCorrect ? 'Correct! 🎉' : 'Not quite right'}
                        </h3>
                      </div>
                      
                      {!isCorrect && (
                        <p className="text-slate-700 mb-2">
                          <span className="font-bold">Correct answer:</span> {question.correct_answer}
                        </p>
                      )}
                      
                      <p className="text-slate-700" data-testid="explanation">
                        <span className="font-bold">Explanation:</span> {question.explanation}
                      </p>
                    </div>

                    {!isCorrect && (
                      <Button
                        onClick={() => setTutorOpen(true)}
                        className="w-full mt-4 bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 rounded-full py-6 text-base font-bold group"
                        data-testid="explain-further-button"
                      >
                        <MessageCircleQuestion className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" strokeWidth={2.5} />
                        Ask Prof.Scaly to Explain Further
                      </Button>
                    )}

                    <div className="flex gap-3 mt-4">
                      <Button
                        onClick={generateNewQuestion}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                        data-testid="next-question-button"
                      >
                        Next Question
                      </Button>
                      {!isSaved && (
                        <Button
                          onClick={saveFavorite}
                          variant="outline"
                          className="rounded-full px-6"
                          data-testid="save-after-result-button"
                        >
                          <Heart className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        )}
      </main>

      {/* AI Tutor Chat Dialog */}
      {question && (
        <TutorChatDialog
          open={tutorOpen}
          onOpenChange={setTutorOpen}
          questionType={moduleId}
          level={user.level}
          questionContext={{
            question_text: question.question_text,
            options: question.options,
            correct_answer: question.correct_answer,
            user_answer: userAnswer,
            explanation: question.explanation,
            topic: question.topic
          }}
        />
      )}
    </div>
  );
};

export default PracticePage;